import { UseFormReturn } from 'react-hook-form';
import {
  edgeDtoToRelationshipFormValues,
  RelationshipFormSyncOpts,
} from '@components/EntityDrawers/drawers/Relationship/edgeDtoToRelationshipFormValues';
import { RelationshipFormValues } from '@components/EntityDrawers/drawers/Relationship/form';
import useResetSelectionAfterSave from '@components/EntityDrawers/hooks/useResetSelectionAfterSave';
import useStore from '@components/MainStage/store';
import { ProjectDrawerTypes } from '@constants/entities/drawers';
import { WeightTypes } from '@constants/entities/relationship';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import { useAppDispatch } from '@hooks/store';
import useAssets from '@hooks/useAssets';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import {
  useCreateRelationshipMutation,
  useUpdateRelationshipMutation,
} from '@store/services/nodes';
import {
  EdgeDTO,
  IUpdateRelationshipRequest,
} from '@store/services/nodes/types';
import { projectsApi } from '@store/services/projects';
import { QueryTags } from '@store/services/tags';
import { cutNumberPeriods, parseErrorResponse } from '@utils/helpers';
import dayjs from 'dayjs';

type SaveRelationshipOptions = {
  form: UseFormReturn<RelationshipFormValues>;
  editMode?: boolean;
  /** Required for syncing the form from API after edit save (shortcut keeps drawer open). */
  formSync?: RelationshipFormSyncOpts;
};

const useSaveRelationship = ({
  form: { getValues, reset },
  editMode,
  formSync,
}: SaveRelationshipOptions) => {
  const activeEdge = useStore((s) => s.edges.find((e) => e.selected));
  const selectEdge = useStore((s) => s.selectEdge);
  const resetSelectionAfterSave = useResetSelectionAfterSave();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const {
    hideDrawer,
    setDrawer,
    toolbox: { mode },
    setHasChanges,
  } = useProject();
  const { selectedAssetId } = useAssets();

  const { saveResponseToRedux } = useSaveEntitiesResponse();

  const [createRelationship, createState] = useCreateRelationshipMutation();
  const [updateRelationship, updateState] = useUpdateRelationshipMutation();

  const mutateRelationship = (
    baseBody: Omit<IUpdateRelationshipRequest, 'id'>,
  ) => {
    if (editMode) {
      return updateRelationship({ id: activeEdge?.id ?? '', ...baseBody });
    }

    return createRelationship({
      ...baseBody,
      child_id: activeEdge?.target || '',
      parent_id: activeEdge?.source || '',
    });
  };

  const handleSave = async (options?: { fromShortcut?: boolean }) => {
    const fromShortcut = Boolean(options?.fromShortcut);

    try {
      if (activeEdge) {
        const relationEdgeId = activeEdge.id;
        const {
          type,
          dependency,
          description,
          weight,
          weight_manual,
          context,
        } = getValues();

        const baseBody = {
          mode,
          assetId: selectedAssetId || undefined,

          type,
          dependency,
          description: description || null,
          weight: parseFloat(
            cutNumberPeriods(parseFloat(weight.replace(',', '.'))),
          ),
          weight_type: weight_manual ? WeightTypes.Manual : WeightTypes.Auto,

          meta_data: { controls: context },

          ...(selectedAssetId && { asset_id: selectedAssetId }),
        } as Omit<IUpdateRelationshipRequest, 'id'>;

        const response = await mutateRelationship(baseBody).unwrap();
        saveResponseToRedux(response);

        // Canvas is already synced from PATCH via saveResponseToRedux.
        // Invalidating ProjectNodes refetches GET /nodes and can overwrite relations with a payload
        // that omits `description` on the first round-trip — UI clears the field until reopen.
        dispatch(projectsApi.util.invalidateTags([QueryTags.Project]));
        setHasChanges(false);

        if (editMode) {
          const updatedDto = response.relations.find(
            (r) => r.id === relationEdgeId,
          );
          if (updatedDto && formSync) {
            reset({
              ...edgeDtoToRelationshipFormValues(
                updatedDto,
                formSync,
                description,
              ),
              maxWeight: getValues().maxWeight,
            });
          }

          showToast(
            fromShortcut
              ? 'Changes saved'
              : 'The relationship has been successfully updated.',
            'success',
          );
          if (!fromShortcut) {
            hideDrawer();
          }

          resetSelectionAfterSave(fromShortcut);
        } else {
          const createdRelation = response.relations
            .toSorted((a: EdgeDTO, b: EdgeDTO) =>
              dayjs(a.updated_at).isBefore(dayjs(b.updated_at)) ? 1 : -1,
            )
            .find(
              (r: EdgeDTO) =>
                r.parent_id === activeEdge.source &&
                r.child_id === activeEdge.target &&
                r.type === type,
            );

          if (createdRelation?.id) {
            selectEdge(createdRelation.id);
          }

          if (fromShortcut && createdRelation && formSync) {
            reset({
              ...edgeDtoToRelationshipFormValues(
                createdRelation,
                formSync,
                description,
              ),
              maxWeight: getValues().maxWeight,
            });
          }

          showToast(
            fromShortcut
              ? 'Changes saved'
              : 'The relationship has been successfully created.',
            'success',
          );

          if (fromShortcut) {
            setDrawer({ type: ProjectDrawerTypes.EditRelationship });
          } else {
            hideDrawer();
          }

          resetSelectionAfterSave(fromShortcut);
        }
      }
    } catch (error: any) {
      showToast(error.message || parseErrorResponse(error), 'error');
    }
  };

  return {
    handleSave,
    isLoading: createState.isLoading || updateState.isLoading,
  };
};

export default useSaveRelationship;
